import {MimeType} from "@reva/shared/mime-type";

import {parseFilenameFromContentDisposition} from "../util/browserUtil";
import {X_ERROR_KEY} from "../util/errorUtil";
import type {ApiCredentials} from "./credentials";
import {credentialsIsAnonymous, credentialsIsBasicAuth, credentialsIsToken} from "./credentials";
import type {DownloadedFile} from "./response";
import {removeAdminDuration} from "./response";
import {InvalidResponseError, WebError} from "./web-error";

const MAX_RETRY_ATTEMPTS = 3;

interface InternalRequestData {
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    credentials: ApiCredentials;
    payload?: any;
    contentType?: MimeType;
    acceptType?: MimeType;
    /** Optionales AbortSignal zum Abbrechen der Anfrage (siehe fetchGET/fetchPOST). */
    signal?: AbortSignal;
}

/**
 * True, wenn der übergebene Fehler ein fetch-/AbortController-Abbruch ist (DOMException "AbortError").
 * Abgebrochene Anfragen sind beim Live-Freitextfilter ein erwartetes Ergebnis (ein neuerer Tastendruck
 * bricht die vorherige laufende Anfrage ab) und müssen still geschluckt statt als Query-Fehler
 * angezeigt werden.
 */
export function isAbortError(e: unknown): boolean {
    return !!e && typeof e === "object" && (e as {name?: unknown}).name === "AbortError";
}

/**
 * True, wenn der Fehler von einem abgelaufenen `AbortSignal.timeout` stammt (DOMException "TimeoutError").
 * Anders als {@link isAbortError} ist das kein erwarteter Abbruch, sondern ein Fehler: der Dienst hat
 * nicht rechtzeitig geantwortet (#19906).
 */
export function isTimeoutError(e: unknown): boolean {
    return !!e && typeof e === "object" && (e as {name?: unknown}).name === "TimeoutError";
}

/**
 * Kodiert einen String UTF-8-korrekt nach Base64.
 *
 * Das nackte `btoa()` erwartet einen "binary string" (ein Zeichen = ein Byte) und deutet jeden
 * Codepoint als Latin-1: `ä` (U+00E4) wird zum einzelnen Byte 0xE4, alles ab U+0100 (z.B. `€`)
 * wirft sogar einen InvalidCharacterError. Die REVA-API dekodiert den Basic-Auth-Header aber als
 * UTF-8 (`BasicAuth.decode` in ib-rest), und BCrypt hasht ebenfalls die UTF-8-Bytes des Passworts.
 * Ein in accounts.immobrain.net gesetztes Passwort mit Umlaut/Sonderzeichen ließ sich dort deshalb
 * weiter benutzen, in REVA aber nie wieder anmelden. Darum hier explizit erst nach UTF-8 kodieren
 * und die Bytes dann einzeln in den von `btoa()` erwarteten binary string legen.
 */
export function base64EncodeUtf8(value: string): string {
    let binary = "";
    for (const byte of new TextEncoder().encode(value)) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

/**
 * Executor class to implement Http Requests with the "Fetch" browser API.
 */
export class ApiExecutor {
    /**
     * GET request with fetch API (JSON data).
     * @param url full query URL
     * @param credentials authentication credentials
     * @param [signal] optionales AbortSignal zum Abbrechen der Anfrage
     * @return response of request
     */
    async fetchGET(url: string, credentials: ApiCredentials, signal?: AbortSignal): Promise<any> {
        const response = await this._fetch(url, {
            method: "GET",
            credentials: credentials,
            signal: signal,
        });
        return this._handleWebResponse(response);
    }

    /**
     * POST request with fetch API (JSON data).
     * @param url full query URL
     * @param credentials authentication credentials
     * @param payload POST body payload
     * @param [signal] optionales AbortSignal zum Abbrechen der Anfrage
     * @return response of request
     */
    async fetchPOST(url: string, credentials: ApiCredentials, payload: any, signal?: AbortSignal): Promise<any> {
        const response = await this._fetch(url, {
            method: "POST",
            credentials: credentials,
            payload: JSON.stringify(payload),
            signal: signal,
        });
        return this._handleWebResponse(response);
    }

    /**
     * PATCH request with fetch API (JSON data).
     * @param url full query URL
     * @param credentials authentication credentials
     * @param payload PATCH body payload
     * @return response of request
     */
    async fetchPATCH(url: string, credentials: ApiCredentials, payload: any): Promise<any> {
        const response = await this._fetch(url, {
            method: "PATCH",
            credentials: credentials,
            payload: JSON.stringify(payload),
        });
        return this._handleWebResponse(response);
    }

    /**
     * PUT request with fetch API (JSON data).
     * @param url full query URL
     * @param credentials authentication credentials
     * @param payload PUT body payload
     * @return response of request
     */
    async fetchPUT(url: string, credentials: ApiCredentials, payload: any): Promise<any> {
        const response = await this._fetch(url, {
            method: "PUT",
            credentials: credentials,
            payload: JSON.stringify(payload),
        });
        return this._handleWebResponse(response);
    }

    /**
     * DELETE request with fetch API (JSON data).
     * @param url full query URL
     * @param credentials authentication credentials
     * @return response of request
     */
    async fetchDELETE(url: string, credentials: ApiCredentials): Promise<any> {
        const response = await this._fetch(url, {
            method: "DELETE",
            credentials: credentials,
        });
        return this._handleWebResponse(response);
    }

    /**
     * GET request with fetch API (Non-JSON data, binary).
     * @param {string} url full query URL
     * @param {ApiCredentials} credentials authentication credentials
     * @param [accept] optional Accept header
     * @param [signal] optionales AbortSignal zum Abbrechen der Anfrage (z.B. `AbortSignal.timeout`)
     * @return response of request as Blob
     */
    async fetchGETBinary(
        url: string,
        credentials: ApiCredentials,
        accept?: MimeType,
        signal?: AbortSignal,
    ): Promise<Blob> {
        const response = await this._fetch(url, {
            method: "GET",
            credentials: credentials,
            acceptType: accept ?? MimeType.ANY,
            signal: signal,
        });
        return this._handleWebBinaryResponse(response);
    }

    /**
     * GET request with fetch API (Non-JSON data, binary), which also return a filename for downloaded file.
     * @param url full query URL
     * @param credentials authentication credentials
     * @param [accept] optional Accept header
     * @return response of request as DownloadedFile
     */
    async fetchGETFileDownload(url: string, credentials: ApiCredentials, accept?: MimeType): Promise<DownloadedFile> {
        const response = await this._fetch(url, {
            method: "GET",
            credentials: credentials,
            acceptType: accept ?? MimeType.ANY,
        });
        return await this._handleFileDownloadResponse(response);
    }

    /**
     * POST request with fetch API (FormData).
     * @param url full query URL
     * @param {ApiCredentials} credentials authentication credentials
     * @param {FormData} payload POST body payload
     * @param [signal] optionales AbortSignal zum Abbrechen der Anfrage
     * @return response of request
     */
    async fetchPOSTFormData(
        url: string,
        credentials: ApiCredentials,
        payload: FormData,
        signal?: AbortSignal,
    ): Promise<any> {
        // Kein contentType: bei FormData muss der Browser den Content-Type-Header
        // selbst setzen, da nur er die multipart-boundary kennt (#18745-Regression).
        const response = await this._fetch(url, {
            method: "POST",
            credentials: credentials,
            payload: payload,
            signal: signal,
        });
        return await this._handleWebResponse(response);
    }

    /**
     * POST request with fetch API, sending a raw binary body (application/octet-stream).
     * Used for the energy-certificate IDP upload, which the downstream service consumes as
     * a raw octet-stream (not multipart).
     * @param url full query URL
     * @param credentials authentication credentials
     * @param body raw binary payload
     * @return response of request (JSON)
     */
    async fetchPOSTBinary(url: string, credentials: ApiCredentials, body: ArrayBuffer | Blob): Promise<any> {
        const response = await this._fetch(url, {
            method: "POST",
            credentials: credentials,
            payload: body,
            contentType: MimeType.OCTET_STREAM,
        });
        return await this._handleWebResponse(response);
    }

    /**
     * Internal Execution of the request with the Fetch Browser API.
     * @param url full query URL
     * @param request request data
     * @return response of request
     */
    async _fetch(url: string, request: InternalRequestData) {
        const headers: Record<string, string> = {
            Accept: request.acceptType ?? MimeType.JSON,
        };
        // #19351: anonymous credentials (password-reset request/redeem) send no Authorization header at all.
        const authorizationHeader = this._authorizationHeader(request.credentials);
        if (authorizationHeader) {
            headers.Authorization = authorizationHeader;
        }
        // FormData nie mit manuellem Content-Type senden: der Browser generiert
        // ihn inklusive der multipart-boundary, ein manueller Header (ohne
        // boundary) macht den Body für den Server unparsebar -> HTTP 400.
        if (request.payload && !(request.payload instanceof FormData)) {
            headers["Content-Type"] = request.contentType ?? MimeType.JSON;
        }
        const init: RequestInit = {
            method: request.method,
            mode: "cors",
            headers: headers,
            redirect: "error",
            cache: "no-cache",
            referrerPolicy: "no-referrer",
            body: request.payload,
            signal: request.signal,
        };
        return this._retryFetch(() => fetch(url, init));
    }

    /**
     * Internal retry logic for fetch requests.
     * @param fetchCallback function to call for the fetch request
     * @param [attempt] current attempt number
     * @return response from the fetch request
     * @private
     */
    async _retryFetch(fetchCallback: () => Promise<Response>, attempt: number = 1): Promise<Response> {
        const response = await fetchCallback();
        if (response.status === 429 && attempt < MAX_RETRY_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            return this._retryFetch(fetchCallback, attempt + 1);
        }
        return response;
    }

    /**
     * Create authorization header from credentials.
     * @param {ApiCredentials} credentials authentication credentials
     * @return {string|null} Authorization header, or null for anonymous credentials (#19351 — no
     *   header is sent at all, e.g. for the unauthenticated password-reset endpoints)
     * @throws Error if credentials are neither anonymous, a valid token nor valid basic auth
     * @private
     */
    _authorizationHeader(credentials: ApiCredentials): string | null {
        if (credentialsIsAnonymous(credentials)) {
            return null;
        }
        // credentialsIsToken/credentialsIsBasicAuth only tell the two credential kinds apart; they do
        // not check validity, hence the additional non-empty checks.
        if (credentialsIsToken(credentials) && credentials.token) {
            return `Bearer ${credentials.token}`;
        } else if (credentialsIsBasicAuth(credentials) && credentials.username && credentials.password) {
            const basicEncoded = base64EncodeUtf8(`${credentials.username}:${credentials.password}`);
            return `Basic ${basicEncoded}`;
        }
        throw new Error(`Invalid credentials: ${JSON.stringify(credentials)}`);
    }

    /**
     * Handle response from API.
     * @param response API response
     * @return Promise with result JSON, or error
     * @private
     */
    async _handleWebResponse(response: Response): Promise<any> {
        if (response.status === 204 && response.headers?.has(X_ERROR_KEY))
            throw new WebError(response.status, null, response.headers);

        const json = await this._readJson(response);
        if (response.status !== 200) {
            throw new WebError(response.status, json);
        }
        // Always remove property "durationMillis" (only for admins) from response
        return removeAdminDuration(json);
    }

    /**
     * Handle response from API, with Non-JSON (Binary) Response.
     * @param response API response
     * @return Promise with result Blob, or error
     * @private
     */
    async _handleWebBinaryResponse(response: Response): Promise<Blob> {
        if (response.status !== 200) {
            // Try to parse JSON error response (e.g., from PDF service)
            let errorData = {statusText: response.statusText};
            try {
                const text = await response.text();
                errorData = JSON.parse(text);
            } catch {
                // If parsing fails, keep default error data
            }
            throw new WebError(response.status, errorData);
        }
        return response.blob();
    }

    /**
     * Handle response from API, with (Binary) File-Download Response.
     * @param response API response
     * @return result file download with blob and filename, or error
     * @private
     */
    async _handleFileDownloadResponse(response: Response): Promise<DownloadedFile> {
        if (response.status !== 200) {
            // Try to parse JSON error response (e.g., from PDF service)
            let errorData = {statusText: response.statusText};
            try {
                const text = await response.text();
                errorData = JSON.parse(text);
            } catch {
                // If parsing fails, keep default error data
            }
            throw new WebError(response.status, errorData);
        }
        const filename = parseFilenameFromContentDisposition(response.headers.get("Content-Disposition"));
        const blob = await response.blob();
        return {blob, filename};
    }

    /**
     * Read JSON from response.
     * @param response API response
     * @return result JSON, or error
     * @private
     */
    async _readJson(response: Response): Promise<any> {
        if (
            (response.status === 204 || response.status === 201) &&
            response.headers.get("Content-Type") !== MimeType.JSON
        ) {
            // No Content in Response
            return {};
        }
        try {
            return await response.json();
        } catch (e) {
            console.log("Invalid response is not a JSON", e);
            throw new InvalidResponseError(response.status);
        }
    }
}
